import React, { useMemo, useState, useEffect, useRef, forwardRef } from "react";
import Verlag from '../../assets/fonts/Verlag-Book.otf';
import axiosWithAuth from "../../helpers/AxiosHelper";
import errorMessage from "../../helpers/errorMessage";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import {Toast} from 'primereact/toast';
import "../../../node_modules/primeflex/primeflex.css";
import 'primeicons/primeicons.css';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
// This page used to load its own separate, unscoped PrimeReact stock theme
// (lara-light-indigo) on top of the shared exaloan-primereact-theme(-dark)
// .css from App.js - always active regardless of dark mode, competing with
// (and for any property our theme doesn't explicitly restate, silently
// overriding) the real theme. Removed - this page uses the same shared
// theme as every other page now, no separate import needed.
import 'primereact/resources/primereact.css';
import "./file_tracking.css";
import { useAuth } from "../../hooks/useAuth";
import { FILE_UPLOAD_COLUMNS } from "./trackColumns";
import { Calendar } from 'primereact/calendar';
import { normalize_platform_name } from "../../helpers/PlatformNameHelper";
import LoadingState from "../common/LoadingState";

const verlag = {
  fontFamily: 'Verlag',
  fontStyle: 'normal',
  fontWeight: 'normal',
  src: `url(${Verlag}) format('OpenType')`,
};

const style = {
  testTransform: "uppercase",
  fontsize: "30px",
  // No hardcoded color here - .text_title's own CSS (file_tracking.css)
  // already sets color: var(--text-color), and an inline style here would
  // win over that class rule regardless of theme (this "History" title was
  // still showing forced black text in dark mode because of exactly that).
};

const TrackFile = () => {
  const [fileData, setFileData] = useState([]);
  const [loading, isLoading] = useState(false)
  const { platform } = useAuth();
  const [columns, setColumns] = useState(FILE_UPLOAD_COLUMNS);
  const [showModal, setShowModal] = useState(false);
  const toast = useRef(null);
  const [myMap, setMyMap] = useState(new Map());
  const updateMap = (k, v) => {
    setMyMap(new Map(myMap.set(k, v)));
  }
  const [expandedRows, setExpandedRows] = useState(null);
  const [platforms_list, setPlatformsList] = useState([]);
  // Keyed by req_id: { loading, data, error } - fetched on demand (not for
  // every row automatically) since most requests never need it, and the
  // underlying query can touch collections with thousands of rows.
  const [pipelineErrors, setPipelineErrors] = useState({});
  useEffect(() => {
    // Only the Exaloan admin view shows a Platform column at all - see
    // get_admin_columns() below - so only it needs the canonical name list.
    if (platform !== 'Exaloan') {
      return;
    }
    axiosWithAuth.get("/platforms")
      .then(data => setPlatformsList(data.data || []))
      .catch(err => console.error('Originator list fetch failed:', errorMessage(err)));
  }, [platform]);
  useEffect(() => {
    isLoading(true)
    const result = axiosWithAuth.get("/files", { params: { Platform: platform } })
    result.then(data => {
      if (platform === 'Exaloan') {
        setColumns(get_admin_columns())
      }
      let fetched_data = data.data
      isLoading(false)
      setFileData([...fetched_data || []].map(d => formatRequestMetadata(d, platforms_list)));
      // setFileData(data.data)
    }).catch(err => {
      console.error('File list fetch failed:', errorMessage(err))
    });
    const interval = setInterval(() => {

      axiosWithAuth.get("/files", { params: { Platform: platform } }).then(data => {
        if (platform === 'Exaloan') {
          setColumns(get_admin_columns())

        }
        // console.log('Inside Interval')
        let fetched_data = data.data
        setFileData([...fetched_data || []].map(d => formatRequestMetadata(d, platforms_list)));
        // setFileData(data.data)
      }).catch(err => {
        console.error('File list refresh failed:', errorMessage(err))
      });
    }, 5000)
    return () => {
      clearInterval(interval)
    }
  }, [platforms_list]); // eslint-disable-line react-hooks/exhaustive-deps



  function formatRequestMetadata(req_metadata, platforms_list_){
    req_metadata.upload_date = new Date(req_metadata.upload_date);
    if (!("uuid" in req_metadata) && ("exaport_req_id" in req_metadata)){
      req_metadata.uuid = req_metadata.exaport_req_id
    }else if (!("uuid" in req_metadata) && ("req_id" in req_metadata)){
      req_metadata.uuid = req_metadata.req_id
    }
    if (("req_id" in req_metadata)&&(req_metadata.req_id.includes("_"))){
      let temp = req_metadata.req_id.split("_")
      req_metadata.uuid = temp[temp.length-1]
    }
    if ("platform" in req_metadata) {
      req_metadata.platform = normalize_platform_name(req_metadata.platform, platforms_list_);
    }
    return req_metadata
  }


  function get_admin_columns(){
    let admin_columns = [
      {
      header: 'Originator',
      field: 'platform',
      sortable: true,
      filter: true,
      }, ...FILE_UPLOAD_COLUMNS,
      {
      header: 'ReqID',
      field: 'uuid',
      sortable:true,
      filter:true,
      // Full UUIDs wrapped across 3 lines was the single biggest thing
      // pushing this table's total width (and every ancestor's, before the
      // min-width:0 fix in main.css) past the page - truncated to one line
      // now, with a dedicated copy button instead of relying on selecting
      // text out of an ellipsis (which doesn't actually work reliably).
      style: { maxWidth: '10rem' },
      body: (rowData) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span title={rowData.uuid} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rowData.uuid}</span>
            <Button type="button" icon="pi pi-copy" className="p-button-text p-button-sm" style={{ padding: '2px', flexShrink: 0 }}
              onClick={(event) => {
                event.stopPropagation();
                navigator.clipboard.writeText(rowData.uuid);
                toast.current.show({ severity: 'success', summary: 'Copied', detail: 'Request ID copied to clipboard', life: 1500 });
              }} />
          </div>
        );
      }
      },
    ]
    return admin_columns
  }

  function showToast(msg){
  }

  function fetchPipelineErrors(reqId) {
    setPipelineErrors(prev => ({ ...prev, [reqId]: { loading: true } }));
    axiosWithAuth.get(`/files/${reqId}/errors`)
      .then(res => {
        setPipelineErrors(prev => ({ ...prev, [reqId]: { loading: false, data: res.data } }));
      })
      .catch(err => {
        setPipelineErrors(prev => ({ ...prev, [reqId]: { loading: false, error: errorMessage(err) } }));
      });
  }

  // The pipeline's own error_message ("Mapper Error::Only 1057 of 1 loans
  // were mapped") reads as if almost nothing was mapped - the "1" is
  // actually a count of loans still stuck in an intermediate staging
  // collection, not a total row count, and num_of_rows (1057 here) is
  // already the successfully-mapped count. Reconstruct a sentence that
  // says what actually happened instead of repeating that confusing string,
  // even to the admin view - refines itself once the detailed breakdown
  // (fetched on demand below) reveals exactly how many loans are stuck.
  function mapperErrorSummary(rowData) {
    const mapped = rowData.num_of_rows
    const stuckCount = pipelineErrors[rowData.req_id]?.data?.stuck?.length
    if (mapped == null) {
      return 'This upload did not complete mapping for all loans - see the details below.'
    }
    if (stuckCount == null) {
      return `${mapped} loan(s) were successfully mapped; at least one loan did not complete mapping. Click "View Row-Level Errors" below for details.`
    }
    const total = mapped + stuckCount
    return `${mapped} of ${total} loan(s) were successfully mapped. ${stuckCount} loan(s) did not complete mapping and need investigation.`
  }

  function renderPipelineErrors(reqId) {
    const state = pipelineErrors[reqId];
    if (!state) {
      return <Button label="View Row-Level Errors" icon="pi pi-search" className="p-button-sm p-button-outlined" onClick={() => fetchPipelineErrors(reqId)} />;
    }
    if (state.loading) {
      return <LoadingState inline label="Loading row-level errors" />;
    }
    if (state.error) {
      return <p className="ex-inline-error">Failed to load row-level errors: {state.error}</p>;
    }
    const data = state.data;
    if (!data.available) {
      return <p className="ex-inline-error">{data.reason}</p>;
    }
    if (data.stuck.length === 0) {
      return <p>Every loan that entered the mapping process also reached the final mapped collection - this status may be from an older, since-superseded run.</p>;
    }
    return (
      <div style={{ marginTop: '0.5em' }}>
        <p style={{ fontWeight: 700 }}>Loans that did not complete mapping ({data.stuck.length}):</p>
        {data.stuck.map(loan => (
          <div key={loan.source_platform_loan_id} style={{ marginBottom: '0.75em' }}>
            <p style={{ marginBottom: '0.25em' }}>Row {loan.row_index} (loan {loan.source_platform_loan_id})</p>
            {loan.errors ? (
              <DataTable value={loan.errors} size="small" showGridlines>
                <Column field="column_name" header="Column" />
                <Column field="msg" header="Issue" />
                <Column field="input" header="Value" body={(r) => String(r.input)} />
              </DataTable>
            ) : (
              <p className="ex-inline-error">No validation error recorded for this loan - it stopped for an unexplained reason.</p>
            )}
          </div>
        ))}
      </div>
    );
  }

  function refreshData() {
    isLoading(true)
    const result = axiosWithAuth.get("/files", { params: { Platform: platform } })
    result.then(data => {
      if (platform === 'Exaloan') {
        setColumns(get_admin_columns())
      }
      let fetched_data = data.data
      setFileData([...fetched_data || []].map(d => formatRequestMetadata(d, platforms_list)));
      // setFileData(data.data)
    }).catch(err => {
      console.error('File list refresh failed:', errorMessage(err))
    }).finally(() => {
      isLoading(false)
    });
  }
    
  let expandedRowStyle = {
    fontSize: 14,
    textAlign: 'left',
    maxWidth: '100%',
    fontWeight: 500
  }

  const rowExpansionTemplate = (data) => {
    // console.log(rowData.rowData);
    let rowData = data;
    if (rowData.status==='Error' || rowData.status==='Rejected')
      return (
        <div
          style={expandedRowStyle}
        >
          {rowData.error_message?<p style={{ width: '100%', wordWrap: 'break-word', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rowData.error_message}</p>:'Server Error. Please Contact Admin'}

          {rowData.error_details && rowData.error_details.msg && <div >
            <p>For file <b className="ft-error-text">{rowData.file_name}</b>, {rowData.error_details.msg}</p>
            {rowData.error_details.columns_affected &&
              <div className="ft-affected-columns">
                {rowData.error_details.columns_affected.map((obj, i) => {
                  return <p className="ft-error-text">{i + 1}. {obj}</p>
                    ;
                })}
              </div>
            }
          </div>}
          {platform==='Exaloan' && rowData.stack_trace &&
              <p className="ft-stack-trace">{rowData.stack_trace.flatMap((val) => [val,<br/>])}</p>
            }

        </div>
      )
    else if (rowData.status === 'Mapper Error')
      return (
        <div style={expandedRowStyle}>
          {platform === 'Exaloan' ? (
            <React.Fragment>
              <p style={{ width: '100%', wordWrap: 'break-word' }}>{mapperErrorSummary(rowData)}</p>
              {renderPipelineErrors(rowData.req_id)}
            </React.Fragment>
          ) : (
            // The raw error_message and per-row validation breakdown are
            // internal pipeline diagnostics (mostly non-blocking schema
            // mismatches the mapping process is meant to absorb) - not
            // something a platform user should have to interpret. Only the
            // Exaloan admin view gets the detail above; everyone else gets a
            // plain status with a way to ask for help.
            <p style={{ width: '100%', wordWrap: 'break-word' }}>
              This file could not be fully processed. Please contact Exaloan support with request ID <b>{rowData.uuid || rowData.req_id}</b> for details.
            </p>
          )}
        </div>
      )
    else
      return (<div
        style={expandedRowStyle}>
        <p style={{ width: '100%', wordWrap: 'break-word', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBlockEnd: "0.5em" }}>
          {rowData.status == "Approved" ? "File has been Approved. And will be uploaded shortly." : rowData.status == "Uploaded" ? "File has been uploaded successfully." : "File has been received and will be approved and uploaded shortly."}
        </p>
        {
          rowData.message &&
          <p className="ft-error-text" style={{ width: '100%', wordWrap: 'break-word', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBlockStart: 0 }}>
            Duplicates Detected: {rowData.message}
          </p>
        }
      </div>)
  }

  // Replaces the old expander-arrow column: same expandedRows shape
  // (object keyed by the _key dataKey) that onRowToggle used to hand back,
  // just toggled from anywhere in the row instead of one small arrow icon.
  const onRowClick = (e) => {
    const key = e.data._key;
    setExpandedRows(prev => {
      const next = { ...(prev || {}) };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = true;
      }
      return next;
    });
  };

  const filters = {
    'name': { value: null, matchMode: FilterMatchMode.STARTS_WITH }
  }

  const dynamicColumns = columns.map((col, i) => {
    return <Column filter={col.filter} key={col.field} field={col.field} header={col.header} body={col.body} sortable={col.sortable} filterElement={col.filterElement}
      dataType={col.dataType} filterField={col.filterField} style={col.style} />;
  });

  function GetTitle(props) {
    const { title } = props;
    return <span className='text_title' style={style}>{title}</span>;
  }

  const renderHeader1 = () => {
    return (
      <div className="ft-header">
        <div className="ft-header-main">
          <span className="ex-page-header-icon" aria-hidden="true"><i className="pi pi-history" /></span>
          <div>
            <GetTitle title="File Upload History" />
            <div className="ex-page-header-subtitle">Every uploaded file and where it is in processing - click a row for details. Refreshes automatically.</div>
          </div>
        </div>
        <div className="ex-toolbar-group">
          <Button type="button" icon="pi pi-filter-slash" label="Clear" className="p-button-outlined" onClick={initFilters1} />
          <Button icon="pi pi-refresh" label="Refresh" onClick={refreshData} />
        </div>
      </div>
    )
  }

  let title = <GetTitle title="File Upload History" />;
  const [filters2, setFilters2] = useState({
    'platform': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'file_name': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'upload_date': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    'status': { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    'data_type': { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    'num_of_rows': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'req_id': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'uuid': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
  });

  const initFilters1 = () => {
    setFilters2({
      'platform': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      'file_name': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      'upload_date': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
      'status': { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
      'data_type': { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
      'num_of_rows': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    });
  }

  return (
    // Was <div><Toast/><div className="content-container">...</div></div> -
    // that outer wrapper was the one thing different from every other page
    // in the app (they all put content-container directly under the
    // layout's Outlet). As an unstyled flex item it couldn't shrink below
    // this table's natural width, so the overflow leaked out to the whole
    // page instead of scrolling inside the card - min-width:0 on
    // .content-container itself (main.css) never got a chance to apply.
    <div className="content-container">
      <Toast ref={toast} position="top-center"/>
      <DataTable value={fileData} header={renderHeader1} showGridlines loading={loading} rowExpansionTemplate={rowExpansionTemplate} expandedRows={expandedRows} onRowClick={onRowClick} dataKey="_key"
        paginator paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}" rows={5} rowsPerPageOptions={[5, 10, 20, 50]} responsiveLayout="scroll" stripedRows sortField="upload_date"
        filterDisplay='menu' filters={filters2} className="file-tracking-table"
      >
        {dynamicColumns}
      </DataTable>
    </div>
  );
}

export default TrackFile;
